import React, { useCallback, useEffect, useState } from "react";
import { apiUrl } from "../taskpane";
import { getWorkbookBytes } from "../excel/openSheetsInNewWindow";

// ── API helpers ──────────────────────────────────────────────────────────────

async function apiFetch(path, token, opts = {}) {
  const res = await fetch(apiUrl(path), {
    ...opts,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(opts.body ? { "Content-Type": "application/json" } : {}),
      ...(opts.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || "მოთხოვნა ვერ შესრულდა");
  return data;
}

// ── Component ────────────────────────────────────────────────────────────────

export default function ExportVersionPage({ token, projects, projectsLoaded, onBack }) {
  const activeProjectId = window.localStorage.getItem("boq.activeProjectId");
  const project = activeProjectId
    ? projects.find((p) => String(p.id) === String(activeProjectId))
    : null;

  const [versions,     setVersions]     = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState("");
  const [newName,      setNewName]      = useState("");
  const [setPrimary,   setSetPrimary]   = useState(false);
  const [creating,     setCreating]     = useState(false);
  const [editId,       setEditId]       = useState(null);
  const [editName,     setEditName]     = useState("");
  const [actionBusy,   setActionBusy]   = useState(null);

  const load = useCallback(async () => {
    if (!project) return;
    setLoading(true); setError("");
    try {
      const data = await apiFetch(`projects/${project.id}/boq/versions`, token);
      setVersions(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [project, token]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newName.trim() || !project) return;
    setCreating(true); setError("");
    try {
      await apiFetch(`projects/${project.id}/boq/versions`, token, {
        method: "POST",
        body: JSON.stringify({ version_name: newName.trim(), set_as_primary: setPrimary }),
      });

      // Download the current workbook as a named snapshot
      const bytes = await getWorkbookBytes();
      const blob = new Blob([bytes], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${project.name} - ${newName.trim()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setNewName(""); setSetPrimary(false);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  };

  const handleRename = async (v) => {
    if (!editName.trim()) return;
    setActionBusy(v.id); setError("");
    try {
      await apiFetch(`projects/${project.id}/boq/versions/${v.id}`, token, {
        method: "PATCH",
        body: JSON.stringify({ version_name: editName.trim() }),
      });
      setEditId(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setActionBusy(null);
    }
  };

  const handleSetPrimary = async (v) => {
    setActionBusy(v.id); setError("");
    try {
      await apiFetch(`projects/${project.id}/boq/versions/${v.id}`, token, {
        method: "PATCH",
        body: JSON.stringify({ set_as_primary: true }),
      });
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setActionBusy(null);
    }
  };

  const handleDelete = async (v) => {
    setActionBusy(v.id); setError("");
    try {
      await apiFetch(`projects/${project.id}/boq/versions/${v.id}`, token, {
        method: "DELETE",
      });
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setActionBusy(null);
    }
  };

  // ── Still loading projects ──
  if (activeProjectId && !project && !projectsLoaded) {
    return (
      <div className="taskpane-root taskpane-root--boq" style={{ padding: "32px 24px" }}>
        <button className="link" onClick={onBack}>← უკან</button>
        <div className="loading-screen" style={{ marginTop: 40 }}>იტვირთება...</div>
      </div>
    );
  }

  // ── No project selected ──
  if (!project) {
    return (
      <div className="taskpane-root taskpane-root--boq" style={{ padding: "32px 24px" }}>
        <button className="link" onClick={onBack}>← უკან</button>
        <div style={{ fontSize: 18, fontWeight: 600, marginTop: 16, marginBottom: 12 }}>
          პროექტი არ არის არჩეული
        </div>
        <div style={{ color: "var(--text-muted)", marginBottom: 20, fontSize: 13 }}>
          ვერსიების სამართავად ჯერ გადადით "პროექტები" განყოფილებაში და გახსენით პროექტი.
        </div>
        <button
          className="primary"
          onClick={onBack}
          style={{ borderRadius: 8, padding: "8px 12px" }}
        >
          პროექტების სია
        </button>
      </div>
    );
  }

  return (
    <div
      className="taskpane-root taskpane-root--boq"
      style={{ padding: "20px 16px" }}
      data-no-translate="true"
    >
      <button className="link" onClick={onBack}>← უკან</button>
      <h2 style={{ marginTop: 16, marginBottom: 2 }}>BOQ ვერსიები</h2>
      <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 20 }}>
        {project.name}
      </p>

      {/* ── Create new version ── */}
      <div style={{
        background: "var(--surface-soft)",
        border: "1px solid rgba(148,163,184,0.2)",
        borderRadius: 8, padding: 14, marginBottom: 20,
      }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10, color: "var(--text-primary)" }}>
          ახალი ვერსია
        </div>
        <input
          type="text"
          placeholder="ვერსიის სახელი (მაგ. ვარიანტი A)"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleCreate()}
          style={{
            width: "100%", padding: "7px 10px", borderRadius: 6, fontSize: 12,
            border: "1px solid rgba(148,163,184,0.3)",
            background: "var(--field-bg)", color: "var(--text-primary)",
            marginBottom: 8, boxSizing: "border-box",
          }}
        />
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12,
          color: "var(--text-muted)", cursor: "pointer", marginBottom: 10 }}>
          <input
            type="checkbox"
            checked={setPrimary}
            onChange={(e) => setSetPrimary(e.target.checked)}
          />
          მთავარ ვერსიად დაყენება
        </label>
        <button
          className="primary"
          onClick={handleCreate}
          disabled={creating || !newName.trim()}
          style={{ width: "100%" }}
        >
          {creating ? "იქმნება..." : "+ შექმნა"}
        </button>
      </div>

      {error && <div className="msg-error" style={{ marginBottom: 12 }}>{error}</div>}

      {/* ── Versions list ── */}
      <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8 }}>
        {loading ? "იტვირთება..." : `${versions.length} ვერსია`}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {versions.map((v) => {
          const busy    = actionBusy === v.id;
          const editing = editId === v.id;
          const date    = v.created_at
            ? new Date(v.created_at).toLocaleDateString("ka-GE")
            : null;

          return (
            <div
              key={v.id}
              style={{
                background: "var(--surface-soft)",
                border: v.is_primary_version
                  ? "1px solid rgba(6,182,212,0.4)"
                  : "1px solid rgba(148,163,184,0.15)",
                borderRadius: 8, padding: "10px 12px",
              }}
            >
              {/* top row */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: editing ? 8 : 0 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{
                      fontSize: 13, fontWeight: 600, color: "var(--text-primary)",
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {v.version_name || "main"}
                    </span>
                    {v.is_primary_version && (
                      <span style={{
                        fontSize: 10, padding: "1px 7px", borderRadius: 10,
                        background: "rgba(6,182,212,0.15)", color: "#06b6d4",
                        border: "1px solid rgba(6,182,212,0.3)", flexShrink: 0,
                      }}>
                        მთავარი
                      </span>
                    )}
                  </div>
                  {date && (
                    <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>
                      {date}
                    </div>
                  )}
                </div>

                {/* actions */}
                {!editing && (
                  <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                    {!v.is_primary_version && (
                      <ActionBtn
                        title="მთავარად დაყენება"
                        onClick={() => handleSetPrimary(v)}
                        disabled={busy}
                      >★</ActionBtn>
                    )}
                    <ActionBtn
                      title="გადარქმევა"
                      onClick={() => { setEditId(v.id); setEditName(v.version_name || ""); }}
                      disabled={busy}
                    >✎</ActionBtn>
                    <ActionBtn
                      title="წაშლა"
                      danger
                      onClick={() => handleDelete(v)}
                      disabled={busy || v.is_primary_version}
                    >✕</ActionBtn>
                  </div>
                )}
              </div>

              {/* rename inline form */}
              {editing && (
                <div style={{ display: "flex", gap: 6 }}>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleRename(v);
                      if (e.key === "Escape") setEditId(null);
                    }}
                    autoFocus
                    style={{
                      flex: 1, padding: "5px 8px", borderRadius: 6, fontSize: 12,
                      border: "1px solid rgba(148,163,184,0.3)",
                      background: "var(--field-bg)", color: "var(--text-primary)",
                    }}
                  />
                  <button
                    onClick={() => handleRename(v)}
                    disabled={actionBusy === v.id || !editName.trim()}
                    style={{
                      padding: "5px 10px", borderRadius: 6, fontSize: 12,
                      background: "#06b6d4", color: "#fff", border: "none", cursor: "pointer",
                    }}
                  >
                    {actionBusy === v.id ? "..." : "შენახვა"}
                  </button>
                  <button
                    onClick={() => setEditId(null)}
                    style={{
                      padding: "5px 8px", borderRadius: 6, fontSize: 12,
                      background: "transparent", color: "var(--text-muted)",
                      border: "1px solid rgba(148,163,184,0.3)", cursor: "pointer",
                    }}
                  >
                    გაუქმება
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ActionBtn({ children, onClick, disabled, danger, title }) {
  return (
    <button
      title={title}
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 26, height: 26, borderRadius: 5, border: "1px solid rgba(148,163,184,0.25)",
        background: "transparent", cursor: disabled ? "not-allowed" : "pointer",
        color: danger ? (disabled ? "rgba(239,68,68,0.3)" : "#ef4444") : "var(--text-muted)",
        fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}
